import { ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash-es';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Defrost, DefrostType } from 'src/app/model/defrost';
import { SensorData, SensorDataGraph } from 'src/app/model/sensorData';
import { DefrostTypeService } from 'src/app/services/defrostType.service';
import { SnackBarService } from 'src/app/services/snackBar.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { ArrayUtils } from 'src/app/utils/arrayUtils';
import { DateUtils } from 'src/app/utils/dateUtils';
import { SensorGraphUtils } from 'src/app/utils/sensorGraphUtils';
import { EventRangeUtils, OffsetAt, SliderValues } from 'src/app/utils/eventRangeUtils';
import { EssayTypeEnum } from '../../../utils/essayUtils';
import { DefrostTypeEnum } from '../../../model/defrost';

import * as Plotly from 'plotly.js-dist-min';
import { ChangeContext, Options } from '@angular-slider/ngx-slider';

export interface DialogDataDefrostEdit {
  defrost: Defrost;
  data: SensorData[];
  idEssayType: number;
  /** Tiempo de ensayo declarado ("dias;horas;minutos"); null si el ensayo no lo tiene. */
  rehearsalTime?: string;
}

@Component({
    selector: 'app-execution-edit-defrost-edit',
    templateUrl: './execution-edit-defrost-edit.component.html',
    standalone: false,
})
export class ExecutionEditDefrostEditComponent implements OnInit, OnDestroy {

  @ViewChildren('rangeChart') rangeChart!: QueryList<ElementRef>;

  startDateStr: string;
  endDateStr: string;

  startDateRange: Date;
  endDateRange: Date;

  types: DefrostType[];

  datasource: SensorDataGraph[];
  sensors: any[];

  valueMin: number;
  valueMax: number;

  firstSlider = false;

  /** Plotly no ha podido pintar la grafica: se avisa en el dialogo (la franja sigue con slider y campos). */
  chartUnavailable = false;

  options: Options = {};

  /** data.data ordenado por fecha (copia): calculateNearDate necesita la serie ordenada. */
  private sortedData: SensorData[] = [];

  /** Valores del slider al empezar a arrastrar, para saber que extremo se ha movido. */
  private sliderBefore: SliderValues = null;

  /** Grafica de Plotly del dialogo, para mover la franja cuando cambian el slider o los campos. */
  private chartEl: any = null;

  /** true mientras se recoloca la franja por codigo, para no tratarlo como un arrastre del usuario. */
  private syncingShape = false;

  private destroyed = false;

  /** Offset de la zona para el guardado; los tests lo cambian por una zona con cambio de hora. */
  offsetAt: OffsetAt = EventRangeUtils.localOffset;

  private destroy$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<ExecutionEditDefrostEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogDataDefrostEdit,
    private defrostTypeService: DefrostTypeService,
    private translate: TranslateService,
    public snackBarService: SnackBarService,
    private spinnerService: SpinnerService,
    private cdr: ChangeDetectorRef) {
    // El dialogo trabaja sobre una copia del evento (el tipo va por ngModel y la franja se escribe
    // al mover el slider): cancelar, o un guardado rechazado, deja intacto el del llamador, que
    // solo recibe el evento nuevo en dialogRef.close.
    this.data = { ...data, defrost: ExecutionEditDefrostEditComponent.copyOf(data.defrost) };
    this.hideCharts();
  }

  private static copyOf(defrost: Defrost): Defrost {
    if (defrost == null) {
      return defrost;
    }

    const copy: Defrost = Object.assign(Object.create(Object.getPrototypeOf(defrost)), defrost);
    copy.startDate = defrost.startDate instanceof Date ? new Date(defrost.startDate.getTime()) : defrost.startDate;
    copy.endDate = defrost.endDate instanceof Date ? new Date(defrost.endDate.getTime()) : defrost.endDate;

    return copy;
  }

  ngOnInit(): void {
    // Al editar un evento existente no se ordenaba, y el ajuste al dato mas cercano saltaba a
    // puntos lejanos. Se ordena una copia para no reordenar el array del llamante.
    this.sortedData = EventRangeUtils.sortedByDate(this.data.data);

    if (ArrayUtils.isNotEmpty(this.sortedData)) {
      if (this.data.defrost != null && this.data.defrost.startDate != null) {
        this.startDateRange = DateUtils.anyToDate(this.data.defrost.startDate);
        this.endDateRange = DateUtils.anyToDate(this.data.defrost.endDate);
      } else {
        // QLOD-52: apertura y corte, el tiempo de ensayo declarado; el resto, 1 h.
        const range = EventRangeUtils.defaultRange(this.sortedData[0].date, this.sortedData[this.sortedData.length - 1].date,
          this.data.rehearsalTime, this.data.idEssayType);
        this.startDateRange = range.start;
        this.endDateRange = range.end;

        const warnings: string[] = [];
        if (range.invalidDeclared) {
          // No se calcula la franja con un tiempo de ensayo que no se entiende: se avisa y se propone 1 h.
          warnings.push(this.translate.instant('executionEdit.events.form.warning.invalidRehearsalTime',
            { value: this.data.rehearsalTime }) as string);
        }
        if (range.end.getTime() === range.start.getTime()) {
          // Todas las lecturas son del mismo instante: no hay franja que proponer (no se guardara vacia).
          warnings.push(this.translate.instant('executionEdit.events.form.warning.noDataExtent') as string);
        }
        if (warnings.length > 0) {
          this.snackBarService.sendError(warnings.join('\n'));
        }
      }

      if (this.startDateStr == null || this.startDateStr === '') {
        this.convertDateToString(this.startDateRange, this.endDateRange);
      }

      this.recalculateDatasource(this.sortedData);
    }
    this.defrostTypeService.findAll().pipe(takeUntil(this.destroy$)).subscribe(item => this.types = item);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();

    // Quita los listeners de Plotly (el de la franja y el resize de responsive), que retienen el componente.
    if (this.chartEl != null) {
      Plotly.purge(this.chartEl);
      this.chartEl = null;
    }
  }

  onOkClick(): void {
    const errs = [];
    const defrost = this.data.defrost;

    // Todo se valida en variables locales: el evento solo se construye si pasa todos los controles.
    // Un campo sin tocar conserva el instante elegido con la franja o el slider (en la hora repetida
    // del cambio de hora, releer el texto podia dar la otra); uno tecleado con una hora ambigua o
    // inexistente no se adivina.
    const start = EventRangeUtils.fieldInstant(this.startDateStr, this.startDateRange);
    const end = EventRangeUtils.fieldInstant(this.endDateStr, this.endDateRange);
    const startDate = start.date;
    const endDate = end.date;

    for (const [field, text] of [[start, this.startDateStr], [end, this.endDateStr]] as [typeof start, string][]) {
      if (field.issue != null) {
        errs.push(this.translate.instant(`executionEdit.events.form.error.${field.issue === 'ambiguous' ? 'ambiguousTime' : 'nonexistentTime'}`,
          { value: text?.trim() }));
      }
    }

    if (startDate == null && start.issue == null) {
      errs.push(this.translate.instant('executionEdit.events.form.error.startDate'));
    }
    if (endDate == null && end.issue == null) {
      errs.push(this.translate.instant('executionEdit.events.form.error.endDate'));
    }
    if (startDate != null && endDate != null && startDate.getTime() > endDate.getTime()) {
      errs.push(this.translate.instant('executionEdit.events.form.error.range'));
    }
    if (defrost.id == null && startDate != null && endDate != null && startDate.getTime() === endDate.getTime()) {
      // Un evento nuevo sin duracion (p. ej. la franja propuesta cuando todas las lecturas son del
      // mismo instante). Los eventos ya guardados no se tocan.
      errs.push(this.translate.instant('executionEdit.events.form.error.emptyRange'));
    }
    if (defrost.idType == null) {
      errs.push(this.translate.instant('executionEdit.events.form.error.type'));
    }

    if (this.data.idEssayType === EssayTypeEnum.ENSAYO_APERTURA_DE_PUERTA) {
       if (defrost.idType === DefrostTypeEnum.CORTE_ELECTRICO) {
        errs.push(this.translate.instant('executionEdit.events.form.openDoor.error.type'));
       }
    }

    if (this.data.idEssayType === EssayTypeEnum.ENSAYO_CORTE_ELECTRICO) {
       if (defrost.idType === DefrostTypeEnum.APERTURA_PUERTA) {
        errs.push(this.translate.instant('executionEdit.events.form.cutElectric.error.type'));
       }
    }

    if (errs.length > 0) {
      this.snackBarService.sendError(errs.join('\n'));

      return;
    }

    // El back recibe el reloj local como si fuera UTC. Con los extremos en distintas ocurrencias
    // de la hora repetida (inicio 02:45 +02:00, fin 02:15 +01:00: 30 min reales) lo enviado
    // quedaria invertido o vacio: no se guarda, se pide ajustar la franja.
    const sentStart = EventRangeUtils.wallClockUtc(startDate.getTime(), this.offsetAt);
    const sentEnd = EventRangeUtils.wallClockUtc(endDate.getTime(), this.offsetAt);

    if (startDate.getTime() < endDate.getTime() && sentStart >= sentEnd) {
      this.snackBarService.sendError(this.translate.instant('executionEdit.events.form.error.clockChange') as string);

      return;
    }

    const result = ExecutionEditDefrostEditComponent.copyOf(defrost);
    result.startDate = new Date(sentStart);
    result.endDate = new Date(sentEnd);

    this.dialogRef.close(result);
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  recalculateDatasource(data: SensorData[]): void {
    this.spinnerService.show();
 
    if (data == null || ArrayUtils.isEmpty(data)) {
      this.datasource = [];
      this.sensors = [];

      return;
    }

    const res: SensorDataGraph[] = [];
    const sensors = [];

    const groupBy = ArrayUtils.groupBy(data, (item: SensorData) => item.date.toISOString());
    groupBy.forEach((values: SensorData[], key: string) => {
      values.forEach(value => {
        const sen = new SensorDataGraph();
        sen.date = DateUtils.anyToDate(key);

        if (!value.equipmentName) {
          return;
        }

        let beautySerialNum;

        if (value.idMultichannel) {
          beautySerialNum = value.multiChannelName?.replace('-', '')?.replace(' ', '').toLowerCase();
        } else {
          beautySerialNum = value.equipmentName?.replace('-', '')?.replace(' ', '').toLowerCase();
        }

        sen[beautySerialNum] = value.value;

        const currentSensors = sensors.map(item => item.name);

        if (!currentSensors.includes(value.equipmentName) && !value.idMultichannel) {
          sensors.push({
            name: value.equipmentName,
            serialNum: value.serialNum,
            value: beautySerialNum
          });
        } else if (value.idMultichannel && !currentSensors.includes(value.multiChannelName)) {
          sensors.push({
            name: value.multiChannelName,
            serialNum: value.serialNum,
            value: beautySerialNum,
          });
        }
      
        res.push(sen);
      });
    });

    this.datasource = res;
    this.sensors = sensors;

    this.data.data.forEach(item => item.date = DateUtils.anyToDate(item.date));

    setTimeout(() => {
      this.renderChartRange();
    }, 2000)

    this.spinnerService.hide();
  }

  customizeLabel = (data) => {
    return data.value.toLocaleString();
  }

  customizeSliderMarker = (data) => {
    return data.value.toLocaleString();
  }

  renderChartRange(): void {
    this.rangeChart.forEach((charts) => {
      if (ArrayUtils.isNotEmpty(this.datasource)) {
        const traces: Plotly.Data[] = this.sensors.map(sensor => {
          const points = SensorGraphUtils.seriesOf(this.datasource, sensor.value as string);
            // QLOD-52: X en epoch (eje lineal, monotono aunque haya cambio de hora); la fecha local
            // del tooltip va en `text`, con el mismo formato que los campos.
            return {
            type: 'scatter',
            mode: 'lines', 
            name: sensor.name,
            x: points.x.map(d => new Date(d).getTime()),
            y: points.y,
            text: points.x.map(d => EventRangeUtils.formatLocal(new Date(d).getTime())),
            hovertemplate: `
              <b>${sensor.name}</b><br>
              %{y}<br>
              %{text}
              <extra></extra>
            `
          }
        });

        const layout: Partial<Plotly.Layout> = {
          height: 500,
          margin: { t: 0, r: 14, l: 16, b: 0 },
          font: {
            size: 10
          },
          xaxis: {
            type: 'linear',
            fixedrange: true,
            // Rango fijo a los datos, el mismo que el slider: con autorange la franja (un evento
            // guardado fuera de los datos) ensanchaba el eje y dejaba de cuadrar con el slider.
            ...this.xRange(),
            tickfont: { size: 8 },
          },
          yaxis: {
            fixedrange: true,
            visible: false
          },
          dragmode: false
        };
        layout.showlegend = false;

        // QLOD-52: paint below the curve; the separate slider rail owns drag/resize
        // so the shape cannot intercept Plotly hover events.
        if (this.startDateRange && this.endDateRange) {
          layout.shapes = [this.rangeShape()];
        }

        if (!this.firstSlider) {
          if (this.startDateRange && this.endDateRange) {
            this.valueMin =  new Date(this.startDateRange).getTime();
            this.valueMax = new Date(this.endDateRange).getTime();

            const bounds = SensorGraphUtils.timeBounds(this.datasource);
            this.options = {
              floor: bounds.min,
              ceil: bounds.max,
              translate: (value: number): string => {
                const date = new Date(value);
                const hours = date.getHours().toString().padStart(2, '0');
                const minutes = date.getMinutes().toString().padStart(2, '0');
                const seconds = date.getSeconds().toString().padStart(2, '0');
                return `${date.getDate()}/${date.getMonth()+1}/${date.getFullYear()} ${hours}:${minutes}:${seconds}`;
              },
              // QLOD-52: se puede estirar cada extremo y arrastrar la franja.
              ...EventRangeUtils.SLIDER_DRAG
            };

            this.firstSlider = true;
          } else {
            this.valueMin = null;
            this.valueMax = null;
          }
        }

        const el = charts.nativeElement;
        this.chartEl = el;

        Plotly.newPlot(el, traces, layout, {
          responsive: true,
          displaylogo: false,
          // One interaction owner: the rail's handles and selection bar. An
          // editable Plotly shape adds another hit layer which masks tooltips.
          edits: { shapePosition: false }
        }).then(() => {
          // newPlot quita los listeners anteriores: se engancha despues de pintar, y solo si el
          // dialogo sigue abierto y este sigue siendo su grafico (si no, ya se ha purgado).
          if (!this.destroyed && this.chartEl === el) {
            el.on('plotly_relayout', (e: any) => this.onShapeRelayout(e));
          }
        }).catch((err: unknown) => this.onChartError(err));

        this.cdr.detectChanges();
      }
    });
  }  

  /**
   * Plotly no ha podido pintar la grafica. La franja sigue editable con el slider y los campos,
   * pero no se ven las lecturas: se registra y se avisa en el dialogo mientras siga abierto.
   */
  onChartError(err: unknown): void {
    console.error('No se ha podido pintar la grafica del evento', err);

    if (!this.destroyed) {
      this.chartUnavailable = true;
      this.cdr.detectChanges();
    }
  }

  onUserChangeStart(change: ChangeContext): void {
    this.sliderBefore = { value: change.value, highValue: change.highValue };
  }

  onUserChange(change: ChangeContext): void {
    // Solo cambia el extremo que se ha movido: el otro conserva lo que diga su campo.
    const moved = EventRangeUtils.movedEnds(this.sliderBefore, { value: change.value, highValue: change.highValue }, this.sortedData);
    this.sliderBefore = null;

    this.applyMovedEnds(moved);
    this.syncShape();
    // ngx-slider 21 updates the painted bar, but not handle ARIA values when
    // dragging the whole range. Refresh through its public options binding.
    this.options = { ...this.options, ariaLabel: this.startDateStr, ariaLabelHigh: this.endDateStr };
  }

  /** La franja se ha arrastrado o estirado sobre la grafica. */
  onShapeRelayout(e: any): void {
    if (this.syncingShape || e == null || this.startDateRange == null || this.endDateRange == null) {
      return;
    }

    const x0 = e['shapes[0].x0'] ?? e.shapes?.[0]?.x0;
    const x1 = e['shapes[0].x1'] ?? e.shapes?.[0]?.x1;

    if (x0 == null && x1 == null) {
      return;
    }

    const before = { value: this.startDateRange.getTime(), highValue: this.endDateRange.getTime() };
    const moved = EventRangeUtils.movedEndsFromShape(before, x0 ?? before.value, x1 ?? before.highValue,
      this.sortedData, this.pixelMs());

    this.applyMovedEnds(moved);
    // Siempre: la franja queda en el dato ajustado, el mismo instante que el slider y los campos.
    this.syncShape();
    this.cdr.detectChanges();
  }

  private applyMovedEnds(moved: { start: Date, end: Date }): void {
    // Los marcadores del slider muestran el mismo instante que los campos de abajo (antes el
    // slider se quedaba en el valor crudo y los campos en el ajustado: :13 arriba, :10 abajo).
    if (moved.start != null) {
      this.startDateRange = moved.start;
      this.valueMin = moved.start.getTime();
      this.data.defrost.startDate = moved.start;
      this.startDateStr = SensorGraphUtils.formatFieldDate(moved.start);
    }

    if (moved.end != null) {
      this.endDateRange = moved.end;
      this.valueMax = moved.end.getTime();
      this.data.defrost.endDate = moved.end;
      this.endDateStr = SensorGraphUtils.formatFieldDate(moved.end);
    }
  }

  /** Lo tecleado a mano mueve el slider y la franja, sin ajustarlo al dato mas cercano. */
  onDateFieldChange(): void {
    const current = this.startDateRange != null && this.endDateRange != null ? { start: this.startDateRange, end: this.endDateRange } : undefined;
    const range = EventRangeUtils.fromFields(this.startDateStr, this.endDateStr, this.options.floor, this.options.ceil, current);

    if (range != null) {
      this.startDateRange = range.start;
      this.endDateRange = range.end;
      this.valueMin = range.start.getTime();
      this.valueMax = range.end.getTime();
      this.syncShape();
    }
  }

  /** Milisegundos que ocupa un pixel del eje X de la grafica (_m: pixeles por ms del eje). */
  private pixelMs(): number {
    const m = this.chartEl?._fullLayout?.xaxis?._m;

    return m > 0 ? 1 / m : 1;
  }

  /** Eje X en epoch: del primer al ultimo dato, con las marcas en hora local; autorange si no hay datos. */
  private xRange(): Partial<Plotly.LayoutAxis> {
    const bounds = SensorGraphUtils.timeBounds(this.datasource);

    if (bounds.min == null || bounds.max == null || !(bounds.max > bounds.min)) {
      return { autorange: true };
    }

    // Sin margen: el slider (ngx-slider) va superpuesto a la grafica con su escala floor/ceil = datos;
    // un margen en el eje los descuadra (~15 px por lado medido en PRE el 21-oct). Para estirar y
    // encoger estan los tiradores del slider.
    return { autorange: false, range: [bounds.min, bounds.max], tickmode: 'array', ...EventRangeUtils.axisTicks(bounds.min, bounds.max) };
  }

  private rangeShape(): Partial<Plotly.Shape> {
    return {
      type: 'rect',
      xref: 'x',
      yref: 'paper',
      x0: this.startDateRange.getTime(),
      x1: this.endDateRange.getTime(),
      y0: 0,
      y1: 1,
      fillcolor: 'rgba(33, 150, 243, 0.15)',
      line: { color: 'rgba(33, 150, 243, 0.9)', width: 2 },
      layer: 'below',
      editable: false
    } as Partial<Plotly.Shape>;
  }

  /** Coloca la franja de la grafica en startDateRange/endDateRange. */
  private syncShape(): void {
    if (this.chartEl?._fullLayout == null || this.startDateRange == null || this.endDateRange == null) {
      return;
    }

    this.syncingShape = true;

    // y0/y1 tambien: Plotly deja mover y estirar la franja en vertical y se quedaria descolocada.
    void Plotly.relayout(this.chartEl, {
      'shapes[0].x0': this.startDateRange.getTime(),
      'shapes[0].x1': this.endDateRange.getTime(),
      'shapes[0].y0': 0,
      'shapes[0].y1': 1
    } as any).finally(() => this.syncingShape = false);
  }

  formatDate(value: string | Date): string {
    const d = new Date(value);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  hideCharts() {
    this.rangeChart?.forEach(elRef => {
      if (elRef?.nativeElement) {
        Plotly.purge(elRef.nativeElement);
      }
    });
  }

  private convertDateToString(startDate: Date, endDate: Date): void {
    this.startDateStr = SensorGraphUtils.formatFieldDate(startDate);
    this.endDateStr = SensorGraphUtils.formatFieldDate(endDate);
  }

}
